import React from 'react';
import { cn } from '../../lib/utils';
import { Delete } from 'lucide-react';

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACK'],
];

function getKeyClasses(state) {
  const base = 'h-[58px] flex items-center justify-center rounded-[4px] font-bold text-[13px] uppercase select-none cursor-pointer transition-all active:scale-95';
  
  switch (state) {
    case 'correct':
      return cn(base, 'bg-[#6aaa64] text-white');
    case 'present':
      return cn(base, 'bg-[#c9b458] text-white');
    case 'absent':
      return cn(base, 'bg-[#787c7e] text-white');
    default:
      return cn(base, 'bg-[#d3d6da] text-black hover:bg-[#c5c8cb]');
  }
}

export function Keyboard({ letterStates, onKeyPress, disabled }) {
  const handleClick = (key) => {
    if (disabled) return;
    onKeyPress(key);
  };

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (disabled) return;
      
      // Don't capture keys if user is typing in an input field
      const activeElement = document.activeElement;
      const isTypingInInput = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      if (isTypingInInput) return;
      
      const key = e.key.toUpperCase();
      
      if (key === 'ENTER') {
        e.preventDefault();
        onKeyPress('ENTER');
      } else if (key === 'BACKSPACE') {
        e.preventDefault();
        onKeyPress('BACK');
      } else if (/^[A-Z]$/.test(key)) {
        e.preventDefault();
        onKeyPress(key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [disabled, onKeyPress]);

  return (
    <div className="w-full max-w-[500px] mx-auto px-2" data-testid="keyboard">
      {KEYBOARD_ROWS.map((row, rowIndex) => (
        <div key={rowIndex} className={cn(
          "flex justify-center gap-[6px] mb-2",
          rowIndex === 1 && "px-[20px]"
        )}>
          {row.map((key) => {
            const isSpecial = key === 'ENTER' || key === 'BACK';
            const state = letterStates[key];
            
            return (
              <button
                key={key}
                onClick={() => handleClick(key)}
                disabled={disabled}
                className={cn(
                  getKeyClasses(state),
                  isSpecial ? 'w-[65px] text-[12px] font-bold' : 'w-[43px]',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
                data-testid={`key-${key.toLowerCase()}`}
                aria-label={key === 'BACK' ? 'Backspace' : key}
              >
                {key === 'BACK' ? <Delete className="w-5 h-5" /> : key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
